// hooks/useAuth.js
import { useState, useEffect } from 'react';
import { 
  auth, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged
} from '../firebase.config';

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Simple login function
  const login = async (email, password) => {
    try {
      setIsLoggingIn(true);
      setError(null);
      
      console.log('Login attempt started for:', email);
      
      // Sign in with Firebase
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      console.log('Login successful:', firebaseUser.email);
      
      // Set user state directly
      setUser(firebaseUser);
      setIsLoggingIn(false);
      
      return firebaseUser;
      
    } catch (error) {
      setIsLoggingIn(false);
      console.error('Login error code:', error.code, error.message);
      
      let errorMessage = 'Login failed';
      
      switch (error.code) {
        case 'auth/user-not-found':
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Invalid password';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many attempts. Try again later';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled';
          break;
        default:
          errorMessage = error.message || 'Login failed';
      }
      
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Simple logout function
  const logout = async () => {
    try {
      console.log('Logging out...');
      await signOut(auth);
      setUser(null);
      setError(null);
    } catch (error) {
      console.error('Logout error:', error);
      setError(error.message);
    }
  };

  // Simple auth state listener
  useEffect(() => {
    console.log('Setting up auth state listener...');
    
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      console.log('Auth state changed:', firebaseUser ? `User: ${firebaseUser.email}` : 'No user');
      
      // Don't update state if we're in the middle of logging in
      if (!isLoggingIn) {
        setUser(firebaseUser);
      }
      
      setIsLoading(false);
    });

    // Clean up
    return () => {
      console.log('Cleaning up auth listener...');
      unsubscribe();
    };
  }, [isLoggingIn]);

  return {
    user,
    isLoading: isLoading || isLoggingIn,
    error,
    login,
    logout
  };
};